import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';
import type { Coordinates } from '../types';

interface UserLocationState {
  location: Coordinates | null;
  isLoading: boolean;
  error: string | null;
  hasPermission: boolean | null;
}

interface UseUserLocationReturn extends UserLocationState {
  getCurrentLocation: () => Promise<void>;
  clearLocation: () => void;
  calculateDistance: (targetLat: number, targetLng: number) => number | null;
}

/**
 * Hook for managing user's GPS location with distance calculations
 */
export const useUserLocation = (): UseUserLocationReturn => {
  const { t } = useTranslation();
  const [state, setState] = useState<UserLocationState>({
    location: null,
    isLoading: false,
    error: null,
    hasPermission: null,
  });

  // Check geolocation permission status
  const checkPermission = useCallback(async () => {
    if (!navigator.permissions) {
      return;
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      setState(prev => ({
        ...prev,
        hasPermission: permission.state === 'granted',
      }));

      permission.onchange = () => {
        const granted = permission.state === 'granted';
        setState(prev => ({
          ...prev,
          hasPermission: granted,
        }));
      };
    } catch (error) {
      logger.error('Permission check failed', 'useUserLocation', error);
    }
  }, []);

  // Get current user location
  const getCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      const error = t('geolocation.notSupported');
      setState(prev => ({ ...prev, error, hasPermission: false }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000, // 5 minutes cache
          });
        }
      );

      const { latitude, longitude } = position.coords;
      const newLocation: Coordinates = { lat: latitude, lng: longitude };

      setState(prev => ({
        ...prev,
        location: newLocation,
        isLoading: false,
        hasPermission: true,
        error: null,
      }));

      logger.info('User location obtained', 'useUserLocation', {
        lat: latitude,
        lng: longitude
      });
    } catch (error) {
      let errorMessage = t('geolocation.error');

      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = t('geolocation.permissionDenied');
            setState(prev => ({ ...prev, hasPermission: false }));
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t('geolocation.positionUnavailable');
            break;
          case error.TIMEOUT:
            errorMessage = t('geolocation.timeout');
            break;
        }
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      logger.error('Geolocation failed', 'useUserLocation', error);
    }
  }, [t]);

  // Clear current location
  const clearLocation = useCallback(() => {
    setState(prev => ({
      ...prev,
      location: null,
      error: null,
    }));
  }, []);

  // Calculate distance between user location and target coordinates (in kilometers)
  const calculateDistance = useCallback((targetLat: number, targetLng: number): number | null => {
    if (!state.location) {
      return null;
    }

    const { lat: userLat, lng: userLng } = state.location;

    // Haversine formula for calculating distance between two points on Earth
    const R = 6371; // Earth's radius in kilometers
    const dLat = (targetLat - userLat) * (Math.PI / 180);
    const dLng = (targetLng - userLng) * (Math.PI / 180);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(userLat * (Math.PI / 180)) * Math.cos(targetLat * (Math.PI / 180)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return Math.round(distance * 100) / 100; // Round to 2 decimal places
  }, [state.location]);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  return {
    ...state,
    getCurrentLocation,
    clearLocation,
    calculateDistance,
  };
};